import type { AnswerEntry, AnswersMap, AnswerValue, QuestionSchema } from './types';

/**
 * Ответы-списки на границе с бэкендом.
 *
 * Сервер хранит ответ типа `list` строкой, склеенной через запятую: пустая
 * строка списка превращалась в хвостовую запятую («Услуга,»), а поле при
 * правке открывалось пустым — строку оно за список не принимало. Пустой
 * список сервер хранит пустой строкой, и выбор «нечего указать» терялся.
 *
 * Поэтому пустые строки не отправляются, «нечего указать» уходит текстом
 * варианта, а прочитанный ответ снова становится массивом. В приложении
 * «нечего указать» по-прежнему пустой список, а не отвеченный вопрос — null.
 */

/** Значение ответа для отправки на сервер. */
export function answerValueForSave(question: QuestionSchema | undefined, value: AnswerValue): AnswerValue {
  if (question?.type !== 'list' || !Array.isArray(value)) return value;

  // Пустой список — только явный выбор «нечего указать»: строки, оставленные
  // пустыми, дают [''], а не []
  if (value.length === 0 && question.allowEmptyAnswer && question.emptyAnswerText) {
    return [question.emptyAnswerText];
  }
  return listItems(value);
}

/** Ответ с сервера в том виде, в каком его ждут поля анкеты. */
export function normalizeAnswer(question: QuestionSchema | undefined, entry: AnswerEntry): AnswerEntry {
  if (question?.type !== 'list') return entry;

  const raw = entry.value;
  const items = typeof raw === 'string' ? raw.split(',') : Array.isArray(raw) ? raw : [];
  const list = listItems(items);

  if (list.length === 0) return { ...entry, value: null };
  if (question.allowEmptyAnswer && list.length === 1 && list[0] === question.emptyAnswerText?.trim()) {
    return { ...entry, value: [] };
  }
  return { ...entry, value: list };
}

export function normalizeAnswers(questions: QuestionSchema[], answers: AnswersMap): AnswersMap {
  const byKey = new Map(questions.map((question) => [question.key, question]));
  return Object.fromEntries(
    Object.entries(answers).map(([key, entry]) => [key, normalizeAnswer(byKey.get(key), entry)])
  );
}

function listItems(items: unknown[]): string[] {
  return items
    .filter((item): item is string => typeof item === 'string')
    .map((item) => item.trim())
    .filter(Boolean);
}
